import React, { useState, useCallback, useMemo } from "react";
import DeerCard from "../../components/DeerCard";
import {
  Box,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Divider,
  Paper,
  Container,
  Grid,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from "@mui/material";
import { Exploration, Reload } from "../../store/user/actions";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { NFT_TYPE } from "../../constant";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Tools.css"; // Reuse the wood-themed styling
import { WorldHeader } from "./WorldHeader";

// Icons
import ForestIcon from "@mui/icons-material/Forest";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import PetsIcon from "@mui/icons-material/Pets";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SortIcon from "@mui/icons-material/Sort";
import CloseIcon from "@mui/icons-material/Close";

const URI_PREFIX = "https://ipfs.neftyblocks.io/ipfs/";
const ABYSSAL_TEMP = "QmesXd95twNg9hyR8NVB2b4ZDB8roCoLfjr7dtD1SZ8NAm";
const MAX_SELECT_COUNT = 50;

// Utility function for rarity comparison
const RARITY_ORDER = {
  Common: 1,
  Uncommon: 2,
  Rare: 3,
  Epic: 4,
  Legendary: 5,
  Mythic: 6,
} as any;

export const Home = ({ hideHeader = false }) => {
  const { loginInfo, gameStatus } = useSelector(
    (state: AppState) => state.userModule
  );
  const dispatch = useDispatch<any>();
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [selectedNfts, setSelectedNfts] = useState<Set<string>>(new Set());
  const [nftType, setNftType] = useState<string>("ALL");
  const [showImage, setShowImage] = useState<boolean>(false);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentWorld = gameStatus.world || "";

  // Filter and sort NFTs by type, availability, rarity, and level
  const filteredNfts = useMemo(() => {
    if (!loginInfo.assets || !Array.isArray(loginInfo.assets)) return [];

    let filtered = loginInfo.assets.filter((item: any) => item.deer_config);

    // Filter by deer type
    if (nftType !== "ALL") {
      filtered = filtered.filter((item: any) => {
        const schemaName = item.schema?.schema_name?.toLowerCase() || "";

        switch (nftType) {
          case "fawn":
            return schemaName === "fawn";
          case "abyssal":
            return schemaName === "abyssal";
          case "og":
            return schemaName === "og.deer";
          case "promo":
            return (
              schemaName === "promo" ||
              item.template?.template_id?.toString() === "859701"
            );
          default:
            return false;
        }
      });
    }

    // Sort first by exploration time, then by rarity, then by level
    return filtered.sort((a: any, b: any) => {
      // First, sort by exploration time
      if (a.exploration_time !== b.exploration_time) {
        return a.exploration_time - b.exploration_time;
      }

      // Then by rarity
      const rarityA = a.data?.Rarity ? RARITY_ORDER[a.data.Rarity] || 0 : 0;
      const rarityB = b.data?.Rarity ? RARITY_ORDER[b.data.Rarity] || 0 : 0;

      if (rarityA !== rarityB) {
        return rarityB - rarityA;
      }

      // Finally by level
      return parseInt(b.level || "1") - parseInt(a.level || "1");
    });
  }, [loginInfo.assets, nftType]);

  // Check if an NFT is explorable
  const isExplorable = useCallback((asset: any) => {
    const currentTime = Math.floor(Date.now() / 1000);
    return (
      asset.deer_config &&
      asset.stakedConfig?.current_explore == 0 &&
      asset.upgrade_end_at == 0 &&
      !selectedNfts.has(asset.asset_id) // Also check it's not already selected
    );
  }, [selectedNfts]);

  // Handle NFT Selection/Deselection
  const handleNFTClick = useCallback((assetId: string) => {
    setSelectedNfts((prevSelected) => {
      const newSelected = new Set(prevSelected);

      if (newSelected.has(assetId)) {
        newSelected.delete(assetId);
      } else if (newSelected.size < MAX_SELECT_COUNT) {
        newSelected.add(assetId);
      } else {
        toast.error(`You can select a maximum of ${MAX_SELECT_COUNT} NFTs.`);
      }

      return newSelected;
    });
  }, []);

  // Handle Select All/Deselect All
  const handleSelectAll = useCallback(() => {
    try {
      setSelectedNfts((prevSelected) => {
        if (
          prevSelected.size === filteredNfts.filter((item: any) => isExplorable(item)).length ||
          prevSelected.size === MAX_SELECT_COUNT
        ) {
          return new Set(); // Deselect all
        } else {
          // Select all explorable filtered NFTs up to the max limit
          const newSelected = new Set<string>();
          let count = 0;
          let max = Math.min(
            filteredNfts.filter((item: any) => isExplorable(item)).length,
            MAX_SELECT_COUNT
          );

          for (const nft of filteredNfts) {
            // Use the isExplorable function which now checks if the NFT is already selected
            if (isExplorable(nft) && count < MAX_SELECT_COUNT) {
              newSelected.add(nft.asset_id);
              count++;
            }
          }

          return newSelected;
        }
      });
    } catch (error) {
      console.error("Error selecting/deselecting NFTs:", error);
      toast.error("An error occurred while selecting/deselecting NFTs.");
    }
  }, [filteredNfts, isExplorable]);

  // Do Exploration
  const doExploration = useCallback(async () => {
    if (!currentWorld) {
      toast.error("Please select a world first.");
      return;
    }
    
    const selectedNftArray = Array.from(selectedNfts);

    if (selectedNftArray.length === 0) {
      toast.error("Please select at least one NFT.");
      return;
    }

    setIsLoading(true);
    
    try {
      // Dispatch and await the result
      const success = await dispatch(Exploration(currentWorld, selectedNftArray));

      if (success) {
        // Clear only the successfully sent NFTs
        setSelectedNfts(new Set());
        toast.success("Exploration started successfully.");
        
        // Force a reload of assets to update exploration status
        dispatch(Reload());
      }
    } catch (error) {
      console.error("Exploration error:", error);
      toast.error("Failed to start exploration. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, currentWorld, selectedNfts]);

  // Handler for NFT type change
  const handleNftTypeChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, value: string) => {
      if (value) {
        setNftType(value);
        setSelectedNfts(new Set()); // Clear selected NFTs when changing type
        setSliderValue(0); // Reset slider when changing type
      }
    },
    []
  );

  // Handler for Show Image Switch Change
  const handleSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowImage(event.target.checked);
    },
    []
  );

  // Calculate Total Fee
  const totalFee = useMemo(() => {
    if (selectedNfts.size === 0) return "0.0000";

    const worldInfo = gameStatus?.reward_info?.find(
      (world: any) => world.id === currentWorld
    );

    if (!worldInfo || !worldInfo.base_fee) return "0.0000";

    const baseFeeParts = worldInfo.base_fee.split(" ");
    const baseFeeValue = parseFloat(baseFeeParts[0] || "0");

    let fee = 0;

    selectedNfts.forEach((assetId) => {
      const nft = loginInfo.assets?.find(
        (asset: any) => asset.asset_id === assetId
      );

      if (!nft) return;

      const costMulti = nft.deer_config?.cost_multi || 1;
      fee += baseFeeValue * costMulti;
    });

    return fee.toFixed(4);
  }, [selectedNfts, loginInfo.assets, gameStatus?.reward_info, currentWorld]);

  // Calculate available deer count - make sure this is accurate
  const availableDeerCount = useMemo(() => {
    if (!loginInfo.assets || !Array.isArray(loginInfo.assets)) return 0;
    
    // Filter to only count deer that are actually available
    return loginInfo.assets.filter((item: any) => 
      item.deer_config && 
      item.stakedConfig?.current_explore == 0 && 
      item.upgrade_end_at == 0
    ).length;
  }, [loginInfo.assets]);

  // Add this handler function
  const handleSliderChange = useCallback(
    (event: Event, newValue: number | number[]) => {
      const value = newValue as number;
      setSliderValue(value);

      // Select top N explorable NFTs
      setSelectedNfts(() => {
        const newSelected = new Set<string>();
        let count = 0;

        for (const nft of filteredNfts) {
          if (isExplorable(nft) && count < value) {
            newSelected.add(nft.asset_id);
            count++;
          }
        }

        return newSelected;
      });
    },
    [filteredNfts, isExplorable]
  );

  // Instructions panel content
  const instructionsContent = (
    <Paper className="game-paper" sx={{ p: 2, mb: 3, position: 'relative', zIndex: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="h6" gutterBottom sx={{ color: '#442a0c', fontWeight: 'bold' }}>
          How Exploration Works
        </Typography>
        <IconButton 
          onClick={() => setShowInstructions(false)}
          size="small"
          sx={{ color: "#442a0c" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 1, bgcolor: 'rgba(68, 42, 12, 0.2)' }} />
      
      <Typography variant="body2" paragraph sx={{ color: '#442a0c' }}>
        Send your Deer NFTs to explore worlds and earn rewards:
      </Typography>
      
      <ol style={{ paddingLeft: "1.5rem", margin: 0, marginBottom: "1rem", color: '#442a0c', fontSize: "0.875rem" }}>
        <li>Select a world to explore from the world selection screen</li>
        <li>Choose which Deer NFTs to send on exploration</li>
        <li>Pay the exploration fee in GRASS tokens</li>
        <li>Wait for the exploration period to complete (typically 24 hours)</li>
        <li>Claim your rewards from the "Claim Explorations" page</li>
      </ol>
      
      <Box sx={{ bgcolor: 'rgba(217, 83, 79, 0.1)', p: 1.5, borderRadius: '8px', border: '1px solid rgba(217, 83, 79, 0.3)', mb: 1, fontSize: "0.875rem" }}>
        <Typography variant="subtitle2" sx={{ color: '#442a0c', fontWeight: 'bold' }}>
          Pro Tip:
        </Typography>
        <Typography variant="body2" sx={{ color: '#442a0c' }}>
          Higher-level and rare Deer have better multipliers, increasing your potential rewards!
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* World Header (Compact) - Only shown if hideHeader is false */}
      {!hideHeader && <WorldHeader onWorldSelect={() => {}} isCompact={true} />}

      {/* Instructions Panel (Togglable) */}
      {showInstructions && instructionsContent}

      {/* Main Content Container */}
      <Paper
        elevation={3}
        className="wood-panel"
        sx={{
          p: {xs: 1.5, sm: 2}, 
          borderRadius: '16px',
          position: 'relative',
          background: 'linear-gradient(135deg, #5c3c10 0%, #4a300b 100%)',
          border: '8px solid #5c3c10',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.3) 0px, rgba(92, 60, 16, 0.3) 2px, transparent 2px, transparent 4px)',
            opacity: 0.5,
            zIndex: 0
          },
          '& > *': {
            position: 'relative',
            zIndex: 1
          }
        }}
      >
        {/* Header with Help Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h6" color="white" fontWeight="bold">
            Select Deer for Exploration
          </Typography>
          
          <Tooltip title="View Instructions">
            <IconButton
              onClick={() => setShowInstructions(!showInstructions)}
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
              size="small"
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {!currentWorld && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2, 
              backgroundColor: 'rgba(255, 193, 7, 0.15)', 
              color: 'white',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              '& .MuiAlert-icon': { color: '#ffcc33' }
            }}
          >
            Please select a world from the world selection above before choosing Deer to explore.
          </Alert>
        )}

        <Paper 
          sx={{ 
            p: 1.5, 
            mb: 2,
            bgcolor: 'rgba(0,0,0,0.2)', 
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1.5} 
            sx={{ mb: 1.5 }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexWrap="wrap"
          >
            {/* NFT Type Filter */}
            <Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                Filter by Type:
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: '8px',
                  padding: '4px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 'unset' },
                  display: 'flex',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  alignItems: 'center',
                  flexWrap: 'wrap', // Always allow wrapping
                  overflowX: 'visible', // Prevent horizontal scroll
                  gap: 1, // Optional: adds spacing between buttons
                }}
              >
                <ToggleButtonGroup
                  color="primary"
                  value={nftType}
                  exclusive
                  onChange={handleNftTypeChange}
                  aria-label="NFT Type"
                  size="small"
                  sx={{
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    width: '100%',
                    '& .MuiToggleButton-root': {
                      color: 'rgba(255,255,255,0.7)',
                      padding: { xs: '4px 6px', sm: '4px 8px' },
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      minWidth: { xs: 60, sm: 80 },
                      '&.Mui-selected': {
                        bgcolor: '#2eca6a',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#25a65a',
                        }
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                      }
                    }
                  }}
                >
                  <ToggleButton value="ALL" aria-label="All NFT Types">
                    All
                  </ToggleButton>
                  <ToggleButton value="fawn" aria-label="Fawn Type">
                    Fawn
                  </ToggleButton>
                  <ToggleButton value="abyssal" aria-label="Abyssal Type">
                    Abyssal
                  </ToggleButton>
                  <ToggleButton value="og" aria-label="OG Type">
                    OG
                  </ToggleButton>
                  <ToggleButton value="promo" aria-label="Promo Type">
                    Promo
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Show Image Switch (only for Abyssal type) */}
            {nftType === "abyssal" && (
              <FormGroup>
                <FormControlLabel
                  sx={{ color: "white", margin: 0 }}
                  control={
                    <Switch
                      checked={showImage}
                      onChange={handleSwitchChange}
                      color="secondary"
                      size="small"
                    />
                  }
                  label="Show Image"
                />
              </FormGroup>
            )}

            {/* Select All Button */}
            <Button
              variant="contained"
              onClick={handleSelectAll}
              startIcon={<ViewModuleIcon />}
              disabled={filteredNfts.length === 0 || currentWorld === ""}
              size="small"
              sx={{
                backgroundColor: '#ffad46',
                color: 'black',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#e69b39' },
                '&.Mui-disabled': { opacity: 0.5 }
              }}
            >
              {selectedNfts.size === filteredNfts.length ||
              selectedNfts.size === MAX_SELECT_COUNT
                ? "Deselect All"
                : `Select All (${Math.min(
                    filteredNfts.filter((item: any) => isExplorable(item)).length,
                    MAX_SELECT_COUNT
                  )})`}
            </Button>
            
            {availableDeerCount > 0 && (
              <Chip 
                label={`${availableDeerCount} Available Deer`}
                color="primary"
                icon={<PetsIcon />}
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Stack>
          
          <Box sx={{ mb: 1, mt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="white">Quick Select:</Typography>
              <Typography variant="body2" color="#ffad46" fontWeight="bold">
                {sliderValue} Deer Selected
              </Typography>
            </Stack>
            
            <Slider
              value={sliderValue}
              onChange={handleSliderChange}
              min={0}
              max={Math.min(availableDeerCount, MAX_SELECT_COUNT)}
              valueLabelDisplay="auto"
              aria-label="Select number of NFTs"
              disabled={!currentWorld}
              size="small"
              sx={{
                color: '#ffad46',
                height: 6,
                '& .MuiSlider-thumb': {
                  backgroundColor: 'white',
                  border: '2px solid #ffad46',
                  width: 14,
                  height: 14
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#ffad46',
                  height: 6
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  height: 6
                },
                '& .MuiSlider-mark': {
                  backgroundColor: 'white',
                },
                '& .MuiSlider-markLabel': {
                  color: 'white',
                },
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
              marks={[
                { value: 0, label: "0" },
                {
                  value: Math.min(availableDeerCount, MAX_SELECT_COUNT),
                  label: Math.min(
                    availableDeerCount,
                    MAX_SELECT_COUNT
                  ).toString(),
                },
              ]}
            />
          </Box>
        </Paper>
        
        {/* Action Controls */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3d2c0a 0%, #302109 100%)',
            border: '4px solid #5c3c10',
            position: 'relative',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="body2" color="white" sx={{ mb: 0.5 }}>
              Selected:
            </Typography>
            <Typography variant="h5" color="#ffad46" fontWeight="bold">
              {selectedNfts.size} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'rgba(255,255,255,0.8)' }}>of {MAX_SELECT_COUNT} max</span>
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            {currentWorld && (
              <>
                <Typography variant="body2" color="white" sx={{ mb: 0.5 }}>
                  Exploration Fee:
                </Typography>
                <Typography variant="h5" color="#e94c58" fontWeight="bold">
                  {totalFee} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'rgba(255,255,255,0.8)' }}>GRASS</span>
                </Typography>
              </>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: { md: '180px' } }}>
            <Button
              variant="contained"
              onClick={doExploration}
              disabled={selectedNfts.size === 0 || !currentWorld || isLoading}
              sx={{
                backgroundColor: '#e94c58',
                color: 'white',
                fontWeight: 'bold',
                py: 1,
                '&:hover': { backgroundColor: '#d43545' },
                '&.Mui-disabled': { backgroundColor: 'rgba(233, 76, 88, 0.3)', color: 'rgba(255,255,255,0.5)' }
              }}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <ForestIcon />}
            >
              {isLoading ? "Starting..." : `Explore World ${currentWorld || "X"}`}
            </Button>
            
            <Typography variant="caption" color="rgba(255,255,255,0.6)" align="center">
              Exploration takes 24 hours
            </Typography>
          </Box>
          
          {/* Wood grain overlay */}
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
            opacity: 0.3, 
            zIndex: 0,
            pointerEvents: 'none',
            borderRadius: '8px'
          }} />
        </Paper>

        {/* NFTs Grid */}
        <Paper 
          elevation={3}
          sx={{
            borderRadius: '12px',
            background: 'rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.1)',
            position: 'relative',
            p: {xs: 1, sm: 1.5}
          }}
        >
          <Typography variant="h6" color="white" fontWeight="bold" sx={{ mb: 1, px: 1 }}>
            Your Deer Collection
          </Typography>
          
          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              borderRadius: "8px",
              p: 0.5,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#5c3c10',
                borderRadius: '4px',
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#e94c58' }} />
              </Box>
            ) : filteredNfts.length > 0 ? (
              <Grid container spacing={1}>
                {filteredNfts.map((item: any) => (
                  <Box
                    key={item.asset_id}
                    sx={{
                      flex: "1 1 200px",
                      maxWidth: { xs: "100%", sm: "48%", md: "23%", lg: "16%" },
                      minWidth: "180px",
                      m: 1,
                      boxSizing: "border-box",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "stretch",
                    }}
                    >
                    <Box
                      sx={{
                      width: "100%",
                      maxWidth: "200px",
                      background: item.exploration_time
                      ? "linear-gradient(135deg, #3d2c0a 0%, #5c3c10 100%)"
                      : item.stakedConfig
                      ? "linear-gradient(135deg, #2c3e0a 0%, #476310 100%)"
                      : "linear-gradient(135deg, #5c3c10 0%, #7d5014 100%)",
                      borderRadius: "8px",
                      p: 1,
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.35)",
                      },
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "stretch",
                      }}
                    >
                  <DeerCard
                      key={item.asset_id}
                      uri={
                        URI_PREFIX +
                        (nftType === "abyssal" && !showImage
                          ? ABYSSAL_TEMP
                          : item.data.img)
                      }
                      name={item.data.name}
                      assetID={item.asset_id}
                      stakedTime={item.exploration_time}
                      isSelected={selectedNfts.has(item.asset_id)}
                      onClick={handleNFTClick}
                      nftData={item}
                      />
                    </Box>
                  </Box>
                ))}
              </Grid>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" color="white" gutterBottom>
                  No Deer NFTs Found
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: "600px", mx: "auto" }}>
                  {nftType !== "ALL" 
                    ? `No ${nftType} type deer found. Try changing the filter.` 
                    : "No deer NFTs found in your wallet. Visit the marketplace to acquire deer NFTs."}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Paper>

      <ToastContainer position="top-right" />
    </Container>
  );
};
